// web/src/api/auth/SSOService.ts
import { AxiosInstance } from 'axios';

export interface SSOProviderResponse {
  sso_provider: string | null;
  error?: string;
}

interface SSOState {
  redirect_uri: string;
}

const buildDefaultRedirectUri = (): string => `${window.location.origin}/home`;

const encodeSSOState = (state: SSOState): string =>
  btoa(JSON.stringify(state))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export default class SSOService {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  /**
   * Check if user has SSO provider configured
   */
  async checkUserSSOProvider(username: string): Promise<string | null> {
    try {
      const response = await this.client.get<SSOProviderResponse>(
        `/v2/authentication/sso/idp/?username=${encodeURIComponent(username)}`,
      );
      return response.data.sso_provider;
    } catch (error) {
      console.error('Failed to check SSO provider:', error);
      throw new Error('Failed to check SSO provider');
    }
  }

  /**
   * Build Cognito authorization URL
   */
  buildCognitoAuthUrl(provider: string, redirectUri?: string): string {
    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_REACT_APP_COGNITO_CLIENT_ID!,
      redirect_uri: import.meta.env.VITE_REACT_APP_COGNITO_REDIRECT_URI!,
      response_type: 'code', // Authorization Code Grant
      scope: 'email openid phone profile',
    });

    if (redirectUri) {
      params.append('state', encodeSSOState({ redirect_uri: redirectUri }));
    }

    // Add identity provider if not default Cognito
    if (provider !== 'COGNITO') {
      params.append('identity_provider', provider);
    }

    return `${import.meta.env.VITE_REACT_APP_COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
  }

  /**
   * Initiate SSO login flow
   */
  async initiateSSOLogin(username: string): Promise<void> {
    // First check if user has SSO provider
    const ssoProvider = await this.checkUserSSOProvider(username);

    if (!ssoProvider) {
      throw new Error('SSO login failed. Please contact your administrator.');
    }

    // Build authorization URL and redirect
    const authUrl = this.buildCognitoAuthUrl(
      ssoProvider,
      buildDefaultRedirectUri(),
    );
    window.location.href = authUrl;
  }
}
